/**
 * @fileoverview Reporting router component with access control.
 */

import React from "react";
import {Route, Navigate} from "react-router-dom";
import {SentryRoutes} from "../utils/sentryRoute";

import {
  PageReportingReportsCheckboxes,
  PageReportingDashboards,
  PageReportingGeneral,
  PageReportingOther,
  PageReportingPermissionsManagement,
  PageReportingPermissionsUserDetail,
  PageReportingPermissionsBulkEdit,
} from "../pages/page-reporting";
import {PageAdmin404} from "../pages/page-admin-404";
import {ConnectedReportContextProvider} from "../pages/page-reporting/ReportContext";
import {AdminHasReportingAccess} from "../models/admin-session/AdminHasReportingAccess";
import {
  DEFAULT_GENERAL_SECTION_ID,
  DEFAULT_OTHER_SECTION_ID,
} from "../pages/page-reporting/constants";

type ReportingRouterRendererProps = Readonly<{
  basePath: string;
}>;

/**
 * Renderer component for reporting routes.
 * Handles routing for different reporting pages and redirects.
 */
export const ReportingRouterRenderer = ({
  basePath,
}: ReportingRouterRendererProps) => (
  <ConnectedReportContextProvider>
    <SentryRoutes>
      <Route
        index
        element={
          <Navigate
            to={
              basePath === "/admin/permissions"
                ? `${basePath}/management`
                : `${basePath}/general/${DEFAULT_GENERAL_SECTION_ID}`
            }
            replace
          />
        }
      />
      <Route
        path="general/:sectionId/:from1/:to1"
        element={<PageReportingGeneral />}
      />
      <Route path="general/:sectionId" element={<PageReportingGeneral />} />
      <Route
        path="general"
        element={<Navigate to={DEFAULT_GENERAL_SECTION_ID} replace />}
      />
      <Route
        path="dashboards/:sectionId"
        element={<PageReportingDashboards />}
      />
      <Route path="dashboards" element={<PageReportingDashboards />} />
      <Route
        path="checkboxes/:sectionId/:from1/:to1"
        element={<PageReportingReportsCheckboxes />}
      />
      <Route
        path="checkboxes/:sectionId"
        element={<PageReportingReportsCheckboxes />}
      />
      <Route path="checkboxes" element={<PageReportingReportsCheckboxes />} />
      <Route
        path="management/bulk"
        element={<PageReportingPermissionsBulkEdit />}
      />
      <Route
        path="management/:userId"
        element={<PageReportingPermissionsUserDetail />}
      />
      <Route
        path="management"
        element={<PageReportingPermissionsManagement />}
      />
      <Route
        path="other/:sectionId/:from1/:to1"
        element={<PageReportingOther />}
      />
      <Route path="other/:sectionId" element={<PageReportingOther />} />
      <Route
        path="other"
        element={<Navigate to={DEFAULT_OTHER_SECTION_ID} replace />}
      />
      <Route path="*" element={<PageAdmin404 />} />
    </SentryRoutes>
  </ConnectedReportContextProvider>
);

/**
 * Reporting router with access control.
 * Guards the reporting routes to ensure only users with reporting access can view them.
 */
export const ReportingRouter = AdminHasReportingAccess(ReportingRouterRenderer);
